import { useState } from 'react';

import { MAX_ASSESSMENT_TITLE_LENGTH } from '@libs/constants';
import { useTrans } from '@libs/i18n';
import { truncateAssessmentTitle } from '@libs/utils';

/**
 * Title field state shared by the two duplicate-assessment dialogs, which
 * render it through `DuplicateTitleField`.
 *
 * Both dialogs stay mounted while closed, so the field is (re)seeded with
 * "Copy of <source title>" on each transition into open — that way a cancelled
 * edit doesn't leak into the next duplication, and confirming without touching
 * the field reproduces the name the backend used to generate on its own.
 *
 * The reset happens during render rather than in an effect: the row menu mounts
 * one of these per assessment on the list page, and an effect would both pay
 * for the prefill on every closed row and paint one frame of the stale title
 * before correcting it. For the same reason nothing here does work — not even a
 * translation lookup — while the dialog is closed.
 */
export const useDuplicateTitleField = (sourceTitle: string, open: boolean) => {
  const { t } = useTrans();

  const buildPrefill = () =>
    truncateAssessmentTitle(t('app.assessments.duplicate.titlePrefill', { title: sourceTitle }));

  const [title, setTitle] = useState(() => (open ? buildPrefill() : ''));
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setTitle(buildPrefill());
  }

  const trimmedTitle = title.trim();

  let error = '';
  if (open) {
    if (!trimmedTitle) {
      error = t('app.assessments.workflow.nameRequired');
    } else if (title.length > MAX_ASSESSMENT_TITLE_LENGTH) {
      error = t('app.assessments.duplicate.titleTooLong', { max: MAX_ASSESSMENT_TITLE_LENGTH });
    }
  }

  return {
    title,
    setTitle,
    trimmedTitle,
    /** Empty string when valid — `DuplicateTitleField` passes it to `helperText`. */
    error,
    isValid: !error,
  };
};
